import axios, {
  type AxiosError,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import { tokenStore } from './tokenStore';
import type { ApiEnvelope, ApiError } from '@/types/api';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

export const apiClient = axios.create({
  baseURL: BASE_URL,
  withCredentials: true, // send the HttpOnly refresh cookie
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

/* ------------------------------------------------------------------ */
/* Request interceptor: inject bearer access token if present.        */
/* ------------------------------------------------------------------ */
apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = tokenStore.get();
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/* ------------------------------------------------------------------ */
/* Response interceptor: on 401, attempt one silent refresh, replay   */
/* the original request. Concurrent 401s share the same refresh call. */
/* ------------------------------------------------------------------ */
type RetryableRequest = AxiosRequestConfig & { __retried?: boolean };

let pendingRefresh: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  if (pendingRefresh) return pendingRefresh;
  pendingRefresh = (async () => {
    try {
      const res = await axios.post<ApiEnvelope<{ accessToken: string }>>(
        `${BASE_URL}/auth/refresh`,
        null,
        { withCredentials: true },
      );
      const token = res.data?.data?.accessToken ?? null;
      tokenStore.set(token);
      return token;
    } catch {
      tokenStore.clear();
      return null;
    } finally {
      pendingRefresh = null;
    }
  })();
  return pendingRefresh;
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiError>) => {
    const original = error.config as RetryableRequest | undefined;
    if (
      error.response?.status === 401 &&
      original &&
      !original.__retried &&
      // skip refresh loop if the failing call IS the refresh
      !original.url?.includes('/auth/refresh')
    ) {
      original.__retried = true;
      const newToken = await refreshAccessToken();
      if (newToken) {
        original.headers = {
          ...original.headers,
          Authorization: `Bearer ${newToken}`,
        };
        return apiClient.request(original);
      }
    }
    return Promise.reject(error);
  },
);

/* ------------------------------------------------------------------ */
/* Convenience helpers that unwrap the API envelope.                  */
/* ------------------------------------------------------------------ */

export async function apiGet<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const res = await apiClient.get<ApiEnvelope<T>>(url, config);
  return res.data.data;
}

export async function apiPost<T, B = unknown>(
  url: string,
  body?: B,
  config?: AxiosRequestConfig,
): Promise<T> {
  const res = await apiClient.post<ApiEnvelope<T>>(url, body, config);
  return res.data.data;
}

export async function apiPut<T, B = unknown>(
  url: string,
  body?: B,
  config?: AxiosRequestConfig,
): Promise<T> {
  const res = await apiClient.put<ApiEnvelope<T>>(url, body, config);
  return res.data.data;
}

export async function apiPatch<T, B = unknown>(
  url: string,
  body?: B,
  config?: AxiosRequestConfig,
): Promise<T> {
  const res = await apiClient.patch<ApiEnvelope<T>>(url, body, config);
  return res.data.data;
}

export async function apiDelete<T = void>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const res = await apiClient.delete<ApiEnvelope<T>>(url, config);
  return res.data.data;
}

export function extractApiError(error: unknown): ApiError | null {
  if (axios.isAxiosError<ApiError>(error)) {
    return (
      error.response?.data ?? {
        title: error.message,
        status: error.response?.status ?? 0,
      }
    );
  }
  return null;
}
